import m from "mithril";
import Requester from "../../shared/request";
import Gateway from "../../shared/app_settings";
import Toasts from "../../shared/toasts";
import i18n from "../../shared/i18n/i18n";
import Modal_Confirm_Delete from "../../shared/components/modal_confirm_delete";
import ModalManagerV2 from "../../shared/modal_v2";
import Auth from "../../shared/auth";
import DateUtilities from "../../shared/utilities/date_utilities";
import Component_Table_Footer from "../../shared/components/component_table_footer";
import RouterService from "../../shared/services/router.service";
import {
    createOption,
    EnrolmentStatus,
    getCourseEnrolmentStatus
} from "../../shared/utilities/courseEnrolmentStatus_utilities";
import Modal_Edit_Enrolment from './modal_edit_enrolment';
import { changeEnrolmentStatusPrompt } from '../../shared/utilities/confirmStatusChange_utilities';

let loading, filter, search, courseId, userId,
    enrolments, callback, selected = [], selectedForResult = [],
    total = 0, limit = 25, offset = 0, orderBy, showExpiry = false;

function submitPagination(lim, off) {
    limit = lim;
    offset = off;
    loadEnrolments();
}

function loadEnrolments(resetOrderBy= false) {
    loading = true;
    enrolments = null;
    selected = [];
    selectedForResult = [];

    let params = [];
    if (filter) {
        params.push("filter=" + filter);
    }
    if (search) {
        params.push("search=" + search);
    }
    if (orderBy) {
        params.push("orderBy=" + orderBy);
    }

    params.push("limit=" + limit);
    params.push("offset=" + offset * limit);
    let endpoint = "courses/enrolments";
    if (courseId) {
        if (resetOrderBy) {
            const orderByIndex = params.findIndex(p => p.startsWith("orderBy="));
            if (orderByIndex !== -1) params.splice(orderByIndex, 1);
        }
        endpoint = "courses/" + courseId + "/enrolments";
    }
    else if (userId) {
        endpoint = "users/" + userId + "/enrolments";
    }

    Requester.get(Gateway.admin + endpoint + (params.length > 0 ? "?" + params.join("&") : ""))
        .then(function (result) {
            total = result.properties.totalCount;
            enrolments = result.entities;
            if (callback) {
                callback(enrolments);
            }
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .finally(function () {
            loading = false;
        });
}

function toggleSelected(id, courseId) {
    // For archive/unarchive
    const selectedIndex = selected.indexOf(id);
    if (selectedIndex === -1) {
        selected.push(id);
    }
    else selected.splice(selectedIndex, 1);

    // For result selection (grouped by courseId)
    let courseItem = selectedForResult.find(x => x.courseId === courseId);
    if (!courseItem) {
        selectedForResult.push({ courseId, ids: [id] });
    } else {
        const idx = courseItem.ids.indexOf(id);
        if (idx === -1) {
            courseItem.ids.push(id);
        } else {
            courseItem.ids.splice(idx, 1);
            if (courseItem.ids.length === 0) {
                selectedForResult = selectedForResult.filter(x => x.courseId !== courseId);
            }
        }
    }
}

function toggleAllSelected() {
    if (selected.length > 0 || selectedForResult.length > 0) {
        selected = [];
        selectedForResult = [];
    } else {
        if (enrolments) {
            for (const enr of enrolments) {
                const id = enr.properties.id;
                const cid = enr.properties.courseId;

                if (!selected.includes(id)) {
                    selected.push(id);
                }

                let courseItem = selectedForResult.find(x => x.courseId === cid);
                if (!courseItem) {
                    selectedForResult.push({courseId: cid, ids: [id]});
                }
                else if (!courseItem.ids.includes(id)) {
                    courseItem.ids.push(id);
                }
            }
        }
    }
}

function changeResult(status, effectiveDate) {
    loading = true;
    Requester.post(Gateway.admin + "courses/enrolments/result", {
        ids: selected,
        selectedCourseWithEnrolments: selectedForResult,
        status: status,
        effectiveDate: effectiveDate
    })
        .then(function () {
            loadEnrolments();
            selected = [];
            selectedForResult = [];
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("cant_save"), error.response.properties.detail);
            loading = false;
        });
}

function submitArchiveEnrolments(status) {
    const isArchived = "archived" === status;
    loading = true;
    Requester.post(Gateway.admin + "courses/enrolments/archive", {
        ids: selected,
        isArchived: isArchived,
        skipUnarchiveLatestActiveEnrolment: true
    })
        .then(function () {
            loadEnrolments();
            selected = [];
            selectedForResult = [];
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("cant_save"), error.response.status.detail);
            loading = false;
        });
}

function orderByUserName() {
    if (orderBy === "userName") {
        orderBy = "userNameDesc";
    }
    else if (orderBy === "userNameDesc") {
        orderBy = null;
    }
    else orderBy = "userName";
    loadEnrolments();
}

function orderByCourseName() {
    if (orderBy === "courseName") {
        orderBy = "courseNameDesc";
    }
    else if (orderBy === "courseNameDesc") {
        orderBy = null;
    }
    else orderBy = "courseName";
    loadEnrolments();
}

function orderByLastActive() {
    if (orderBy === "lastActiveDate") {
        orderBy = "lastActiveDateDesc";
    }
    else if (orderBy === "lastActiveDateDesc") {
        orderBy = null;
    }
    else {
        orderBy = "lastActiveDate";
    }
    loadEnrolments();
}

function orderByCompletion() {
    if (orderBy === "completionDate") {
        orderBy = "completionDateDesc";
    }
    else if (orderBy === "completionDateDesc") {
        orderBy = null;
    }
    else {
        orderBy = "completionDate";
    }
    loadEnrolments();
}

function getSelectedEnrolments() {
    if (!Array.isArray(enrolments) || !Array.isArray(selected)) {
        return [];
    }
    return enrolments.filter(enr => enr && selected.includes(enr.properties.id));
}

function showChangeEnrolmentStatusWithDatePrompt(callback, status) {
    ModalManagerV2.open(i18n.t("edit_enrolment"), [], Modal_Edit_Enrolment, {
        enrolments: getSelectedEnrolments(),
        status: status,
        confirmCallback: callback
    });
}

function populateChangeResultOptions() {
    const optionsMap = {
        notStarted: createOption(EnrolmentStatus.NOT_STARTED, changeEnrolmentStatusPrompt, changeResult),
        passed: createOption(EnrolmentStatus.PASSED, showChangeEnrolmentStatusWithDatePrompt, changeResult),
        failed: createOption(EnrolmentStatus.FAILED, changeEnrolmentStatusPrompt, changeResult),
        archive: createOption(EnrolmentStatus.ARCHIVED, changeEnrolmentStatusPrompt, submitArchiveEnrolments),
        unArchive: createOption(EnrolmentStatus.UN_ARCHIVED, changeEnrolmentStatusPrompt, submitArchiveEnrolments),
    };

    let options = [];
    if (Auth.validateRights(["enrolments_edit"])) {
        if (filter !== "archived") {
            options = [optionsMap.notStarted, optionsMap.passed, optionsMap.failed];
            if (Auth.validateRights([Auth.rights.enrolments_delete])) {
                options.push(optionsMap.archive);
            }
        } else {
            options = [optionsMap.unArchive];
        }
    } else if (Auth.validateRights(["enrolments_reset"]) && filter !== "archived") {
        options = [optionsMap.notStarted];
    }

    return options.length
        ? m(".dropdown", { class: selected.length === 0 ? "disabled" : "" },
            m("button.with-icon pl-2", [
                m("i.icon-edit"),
                i18n.t("change_result"),
                m("i.icon-back rotate-270 ml-2"),
            ]),
            m(".dropdown-list", options)
        )
        : null;
}

const Component_Enrolments = {
    oninit: function (vnode) {
        callback = vnode.attrs.callback;
        search = vnode.attrs.search;
        courseId = vnode.attrs.courseId;
        userId = vnode.attrs.userId;
        showExpiry = vnode.attrs.showExpiry;
        loadEnrolments();
    },
    view: function () {
        return [
            m(".content-box", [
                m(".toolbar", { class: loading ? "disable-buttons" : "" }, [
                    m(".toolbar-section", [
                        populateChangeResultOptions(),
                        selected && selected.length > 0 ? m("span.ml-1", [selected.length, i18n.t("space_selected")]) : ""
                    ]),
                    m(".toolbar-section", [
                        m(".dropdown",
                            m("button.with-icon pl-2", [
                                filter === "passed" ? i18n.t("passed") :
                                    filter === "in_progress" ? i18n.t("in_progress") :
                                        filter === "not_started" ? i18n.t("not_started") :
                                            filter === "not_passed" ? i18n.t("failed") :
                                                filter === "archived" ? i18n.t("archived") : i18n.t("all_enrolments"),
                                m("i.icon-back rotate-270 ml-2")
                            ]),
                            m(".dropdown-list", [
                                m("div", {
                                    class: !filter ? "active" : "", onclick: function () {
                                        filter = null;
                                        loadEnrolments();
                                    }
                                }, i18n.t("all_enrolments")),
                                m(".label-circle label-success", {
                                    class: filter === "passed" ? "active" : "",
                                    onclick: function () {
                                        filter = "passed";
                                        loadEnrolments();
                                    }
                                }, i18n.t("passed")),
                                m(".label-circle label-warning", {
                                    class: filter === "in_progress" ? "active" : "",
                                    onclick: function () {
                                        filter = "in_progress";
                                        loadEnrolments();
                                    }
                                }, i18n.t("in_progress")),
                                m(".label-circle", {
                                    class: filter === "not_started" ? "active" : "",
                                    onclick: function () {
                                        filter = "not_started";
                                        loadEnrolments();
                                    }
                                }, i18n.t("not_started")),
                                m(".label-circle label-error", {
                                    class: filter === "not_passed" ? "active" : "",
                                    onclick: function () {
                                        filter = "not_passed";
                                        loadEnrolments();
                                    }
                                }, i18n.t("failed")),
                                Auth.validateRights([Auth.rights.enrolments_archived_view],
                                    m(".label-circle label-info", {
                                        class: filter === "archived" ? "active" : "",
                                        onclick: function () {
                                            filter = "archived";
                                            loadEnrolments();
                                        }
                                    }, i18n.t("archived")))
                            ])
                        ),
                        m(".has-icon-right", [
                            m("input[type=search]", {
                                oninput: function () { search = this.value; },
                                onkeyup: function (e) { if (e.keyCode === 13) {
                                    loadEnrolments(true);
                                }},
                                value: search, placeholder: i18n.t("search")
                            }),
                            m("i.icon-x c-hand", { onclick: function () { search = null; loadEnrolments(true); } }),
                            m("i.icon-search c-hand", { onclick: function () { loadEnrolments(true); } })
                        ])
                    ])
                ]),
                m(".flex-column", [
                    !loading ? [
                        enrolments ? [
                            m(".table-wrapper flex-auto", [
                                enrolments.length > 0 ? [
                                    m("table.full-width", [
                                        m("thead",
                                            m("tr", [
                                                m("th", m(".toolbar-checkbox", { onclick: toggleAllSelected }, m("i", { class: selected.length > 0 ? "icon-checkbox-checked" : "icon-checkbox-empty" }))),
                                                m("th.c-hand", { onclick: orderByUserName }, [i18n.t("user"), m("i.icon-forward align-middle", { class: orderBy === "userName" ? "rotate-90" : orderBy === "userNameDesc" ? "rotate-270" : "rotate-90 translucent" })]),
                                                m("th.c-hand", { onclick: orderByCourseName }, [i18n.t("course"), m("i.icon-forward align-middle", { class: orderBy === "courseName" ? "rotate-90" : orderBy === "courseNameDesc" ? "rotate-270" : "rotate-90 translucent" })]),
                                                m("th", i18n.t("enrolment_version")),
                                                m("th", i18n.t("result")),
                                                m("th.c-hand", { onclick: orderByLastActive }, [i18n.t("last_active"), m("i.icon-forward align-middle", { class: orderBy === "lastActiveDate" ? "rotate-90" : orderBy === "lastActiveDateDesc" ? "rotate-270" : "rotate-90 translucent" })]),
                                                m("th.c-hand", { onclick: orderByCompletion }, [i18n.t("completed"), m("i.icon-forward align-middle", { class: orderBy === "completionDate" ? "rotate-90" : orderBy === "completionDateDesc" ? "rotate-270" : "rotate-90 translucent" })]),
                                                showExpiry ? m("th", i18n.t("expiry_date")): '',
                                            ])
                                        ),
                                        m("tbody", enrolments.map(function (enr) {
                                            const id = enr.properties.id;
                                            const cid = enr.properties.courseId;
                                            return [
                                                m("tr", [
                                                    m("td.list-checkbox c-hand pl-1 pr-1", {
                                                        onclick: function () { toggleSelected(id, cid); },
                                                        style: "width: 18px;"
                                                    }, m("i", { class: selected.includes(id) ? "icon-checkbox-checked" : "icon-checkbox-empty" })),
                                                    m("td.text-bolder", m(m.route.Link, { href: RouterService.getEnrolmentUrl(id) }, enr.properties.firstName ? (enr.properties.firstName + " " + enr.properties.lastName) : m(".text-gray", i18n.t("invited")))),
                                                    m("td", m(m.route.Link, { href: RouterService.getEnrolmentUrl(id), tabindex: "-1" }, enr.properties.courseName)),
                                                    m("td", m(m.route.Link, { href: RouterService.getEnrolmentUrl(id), tabindex: "-1" }, enr.properties.courseVersion)),
                                                    m("td", m(m.route.Link, { href: RouterService.getEnrolmentUrl(id), tabindex: "-1" }, getCourseEnrolmentStatus(enr, true, loading))),
                                                    m("td", m(m.route.Link, { href: RouterService.getEnrolmentUrl(id), tabindex: "-1" }, DateUtilities.getFormattedDateFromUTC(enr.properties.lastActiveDate))),
                                                    m("td", m(m.route.Link, { href: RouterService.getEnrolmentUrl(id), tabindex: "-1" }, DateUtilities.getFormattedDateFromUTC(enr.properties.completionDate))),
                                                    showExpiry ? m("td", m(m.route.Link, { href: RouterService.getEnrolmentUrl(id), tabindex: "-1" }, enr.properties.selfEnrolExpiryDate != null ? DateUtilities.getFormattedDateFromUTC(enr.properties.selfEnrolExpiryDate) : '')) : ''
                                                ])
                                            ];
                                        }))
                                    ])
                                ] : m(".text-gray padding", i18n.t("no_enrolments"))
                            ]),
                            m(Component_Table_Footer, {
                                callback: submitPagination,
                                total: total,
                                limit: limit,
                                offset: offset,
                                loading: loading,
                                item: "enrolments"
                            })
                        ] : m(".text-gray padding", i18n.t("unable_load_enrolments"))
                    ] : m(".text-gray padding", [i18n.t("loading"), m(".loading-dots")])
                ])
            ])
        ];
    }
};

export default Component_Enrolments;
